import type { CampoFecha, ContextoDeRespuesta, Respuesta } from '@pasos/schemas';
import { schemaDelCampo } from '@pasos/schemas';
import { useId, useState } from 'react';
import { PACIENTE } from '../../copy/index.js';
import { LOCALE } from '../../lib/locale.js';

type FechaProps = {
  campo: CampoFecha;
  valor: string | undefined;
  onElegir: (valor: Respuesta | undefined) => void;
  etiquetadoPor: string;
  /** Hoy y el día del egreso: el rango del selector. */
  contexto: ContextoDeRespuesta;
};

/** Una fecha ISO en palabras: "1 de septiembre de 2026". */
export function formatearFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-').map(Number);
  if (!anio || !mes || !dia) return iso;
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(anio, mes - 1, dia)));
}

function esValida(campo: CampoFecha, valor: string, contexto: ContextoDeRespuesta): boolean {
  return schemaDelCampo(campo, contexto).safeParse(valor).success;
}

function problemaDe(
  campo: CampoFecha,
  valor: string,
  contexto: ContextoDeRespuesta,
): string | null {
  if (valor === '' || esValida(campo, valor, contexto)) return null;
  if (campo.noFutura && contexto.hoy && valor > contexto.hoy)
    return PACIENTE.formulario.fechaFutura;
  return PACIENTE.formulario.fechaAnteriorAlEgreso;
}

/**
 * Un día del calendario (E3.25): la fecha de una reinternación.
 *
 * **El selector nativo**, `type="date"`: en el teléfono abre la rueda o el
 * calendario del sistema, que la persona ya conoce, que el lector de pantalla
 * sabe anunciar y que no deja escribir un 31 de febrero. Los límites del rango
 * (hoy, el egreso) van como `min` y `max`, así el selector ni ofrece lo que no
 * vale; y se validan igual, porque en algunos navegadores se puede tipear.
 */
export function Fecha({ campo, valor, onElegir, etiquetadoPor, contexto }: FechaProps) {
  const id = useId();
  const [elegida, setElegida] = useState(valor ?? '');
  const problema = problemaDe(campo, elegida, contexto);

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`flex min-h-15 items-center rounded-lg bg-card px-4 outline-2 -outline-offset-2 focus-within:shadow-halo-navy-100 focus-within:outline-ring ${
          problema ? 'outline-destructive' : 'outline-input'
        }`}
      >
        <input
          aria-describedby={problema ? `${id}-error` : undefined}
          aria-invalid={problema ? true : undefined}
          aria-labelledby={etiquetadoPor}
          className="min-w-0 flex-1 border-0 bg-transparent text-xl font-bold text-heading outline-none focus-visible:outline-none"
          onChange={(evento) => {
            const nueva = evento.target.value;
            setElegida(nueva);
            onElegir(nueva && esValida(campo, nueva, contexto) ? nueva : undefined);
          }}
          type="date"
          value={elegida}
          {...(campo.noFutura && contexto.hoy ? { max: contexto.hoy } : {})}
          {...(campo.desdeElDiaCero && contexto.diaCero ? { min: contexto.diaCero } : {})}
        />
      </div>
      {problema ? (
        <p className="m-0 text-base font-bold text-destructive-fg" id={`${id}-error`} role="alert">
          {problema}
        </p>
      ) : null}
    </div>
  );
}
